import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, generatePath } from 'react-router-dom';

import Button from 'components/Button';
import Container from 'components/Container';
import Pagination from 'components/Pagination';
import Modal from 'components/Modal';
import VideoPlayer from 'components/VideoPlayer';

import { useAppContext } from 'services/context';
import { useTranslation } from 'services/hooks';
import { COURSES_ACTIONS } from 'services/reducers/actionTypes';
import { COURSE_TYPES, ROUTES } from 'constants/index';

import './style.css';

function Chapter() {
	const { chapterId, recordId } = useParams();
	const navigate = useNavigate();
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'chapter';

	const { courses, Api, user, dispatchCourses } = useAppContext();
	const [currentChapter, setCurrentChapter] = useState(undefined);
	const [nextChapter, setNextChapter] = useState(undefined);
	const [paramChapterId, setParamChapterId] = useState();
	const [isVisible, setIsVisible] = useState(false);

	const passedAllTest = useMemo(() => {
		if (!courses?.courses) return false;
		const currentCourse = courses?.courses.find(course => course.recordId === parseInt(recordId));
		const chaptersWithoutFinal = currentCourse?.courseChapters?.filter(chapter => !chapter.isFinal);
		const passed = chaptersWithoutFinal?.every(chapter => chapter.courseChapterStatus >= 2 || !chapter.testUrl);
		return Boolean(passed);
	}, [courses]);

	const isCurrentChapterIsFinal = useMemo(() => {
		if (!currentChapter) return false;
		return Boolean(currentChapter?.isFinal);
	}, [currentChapter]);

	const isDisableESLPagination = useMemo(() => {
		const currentCourse = courses?.courses.find(course => course.recordId === parseInt(recordId));
		if (currentCourse.courseTypeId === COURSE_TYPES.ESL && isCurrentChapterIsFinal && !currentChapter.isOccured) return true;
		return false;
	}, [currentChapter]);

	const isAllowedToProceedToTheNextChapter = useMemo(() => {
		//If passed the test courseChapterStatus should be 2 or more so proceed next chapter without quiz.
		return currentChapter?.courseChapterStatus >= 2 || !currentChapter?.testURL;
	}, [currentChapter, courses?.currentCourse]);

	const totalChaptersLength = useMemo(() => {
		let count = 0;
		if (courses.currentCourse.units) {
			count =
				courses.currentCourse.units.reduce((acc, u) => acc + u.courseChapters.length, 0) + courses.currentCourse.courseChapters.length;
			return count;
		}
		return courses.currentCourse.courseChapters.length;
	}, []);

	const _handleUpdateChapter = id => {
		try {
			let chapter = courses.currentCourse.courseChapters.find(chap => chap.courseChapterId === id);

			if (!chapter) {
				courses.currentCourse.units.map(u => {
					if (chapter) return;
					chapter = u?.courseChapters.find(chap => chap.courseChapterId == id);
				});
			}

			setNextChapter(chapter);

			if (isAllowedToProceedToTheNextChapter) {
				_proceedToNextChapter(chapter);
			} else setIsVisible(true);
		} catch (error) {}
	};

	//proceed to next chapter without quiz
	const _proceedToNextChapter = chapterToProceed => {
		const _chapterId = chapterToProceed?.courseChapterId ? chapterToProceed.courseChapterId : nextChapter.courseChapterId;

		const path = generatePath(`/${ROUTES.COURSE}/:recordId/${ROUTES.CHAPTER}/:chapterId`, {
			recordId,
			chapterId: _chapterId,
		});
		navigate(path);
		setIsVisible(false);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	//proceed to current chapter quiz
	const _handleGotoQuiz = () => {
		const path = generatePath(currentChapter.testURL, {
			recordId: currentChapter.recordId,
			chapterId,
		});
		navigate(path);
		setIsVisible(false);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	useEffect(() => {
		let chapter;

		try {
			if (paramChapterId === chapterId) return;
			chapter = courses.currentCourse.courseChapters.find(chap => chap.courseChapterId == chapterId);
			if (!chapter) {
				courses.currentCourse.units.map(u => {
					if (chapter) return;
					chapter = u?.courseChapters.find(chap => chap.courseChapterId == chapterId);
				});
			}
			if (!chapter) return;
			setParamChapterId(chapterId);
			setCurrentChapter(chapter);
		} catch (error) {
			console.warn(error.message);
		}
	}, [chapterId]);

	//update chapter progress
	useEffect(() => {
		async function updateChapterProgress() {
			const { userId } = user;
			const { courseChapterId } = currentChapter;

			if (!userId || !courseChapterId) {
				console.warn('Unable to update progress on chapter ' + courseChapterId);
				return;
			}
			const isUpdated = await Api.UpdateChapterStatus(courses.currentCourse.recordId, courseChapterId);
			if (!isUpdated) return;

			const userCourses = await Api.GetUserCourses();
			if (userCourses?.length > 0) {
				dispatchCourses({ type: COURSES_ACTIONS.SET_COURSES, payload: userCourses });
			}
		}

		if (currentChapter?.courseChapterStatus === null) updateChapterProgress();
	}, [currentChapter, courses]);

	const _handlePressbackButton = () => {
		try {
			const path = generatePath(`/${ROUTES.COURSE}/:recordId`, {
				recordId: courses.currentCourse.recordId,
			});
			navigate(path);
		} catch (error) {}
	};

	if (!currentChapter || !courses.currentCourse) return null;

	return (
		<Container className="Chapter">
			<h2>{courses.currentCourse.courseName || ''}</h2>
			<div className="pagination_wrapper">
				<Button
					small
					title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_main_button`)}
					startIcon="clipboardIcon"
					onClick={_handlePressbackButton}
					extraClassName="transparent"
				/>
				<Pagination
					chapterOrderInCourse={currentChapter.chapterOrderInCourse}
					currentChapter={currentChapter.courseChapterId}
					handleUpdateChapter={_handleUpdateChapter}
					totalChapters={totalChaptersLength}
					disabledNextChpater={isCurrentChapterIsFinal}
				/>
			</div>
			<div className="wrapper">
				<div dangerouslySetInnerHTML={{ __html: currentChapter.chapterBodyText || '' }}></div>
				<VideoPlayer
					src={currentChapter.chapterFileURL || ''}
					title={currentChapter?.chapterName || ''}
					courseId={courses.currentCourse.courseId || null}
				/>
				{currentChapter.testURL && (
					<Button
						small
						title={currentChapter.testHeaderText}
						onClick={_handleGotoQuiz}
						disabled={isDisableESLPagination || (isCurrentChapterIsFinal && !passedAllTest)}
					/>
				)}
			</div>
			<div className="pagination_wrapper">
				<Button
					small
					title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_main_button`)}
					startIcon="clipboardIcon"
					onClick={_handlePressbackButton}
					extraClassName="transparent"
				/>
				<Pagination
					chapterOrderInCourse={currentChapter.chapterOrderInCourse}
					currentChapter={currentChapter.courseChapterId}
					handleUpdateChapter={_handleUpdateChapter}
					totalChapters={totalChaptersLength}
					disabledNextChpater={isCurrentChapterIsFinal}
				/>
			</div>

			<Modal isVisible={isVisible} hideCloseButton>
				<div className="modal_content">
					<div className="modal_title">{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_title`)}</div>
					<div className="buttons_wrapper">
						<Button
							small
							title={currentChapter.testHeaderText}
							onClick={_handleGotoQuiz}
							disabled={isDisableESLPagination || (isCurrentChapterIsFinal && !passedAllTest)}
						/>
						<Button
							small
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_modal_button`)}
							type="outline"
							onClick={_proceedToNextChapter}
						/>
					</div>
				</div>
			</Modal>
		</Container>
	);
}

export default Chapter;
